import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import {
  useCompanyRegistration,
  RegistrationStep,
} from '../../hooks/useCompanyRegistration';
import { CompanyInfoStep } from './CompanyInfoStep';
import { ContactInfoStep } from './ContactInfoStep';
import { ConfirmationStep } from './ConfirmationStep';
import { RegistrationSuccessDialog } from './RegistrationSuccessDialog';

interface CompanyRegistrationFormProps {
  onSuccess?: (companyId: number) => void;
  onError?: (error: string) => void;
}

const steps = ['企業情報', '代表者情報', '確認・同意'];

export const CompanyRegistrationForm: React.FC<
  CompanyRegistrationFormProps
> = ({ onSuccess, onError }) => {
  const {
    register,
    handleSubmit,
    errors,
    isValid,
    watchedValues,
    currentStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    isSubmitting,
    submitError,
    showSuccessDialog,
    registeredCompanyId,
    handleFieldFocus,
    handleSuccessDialogClose,
    clearSubmitError,
  } = useCompanyRegistration();

  // 次へボタンの処理
  const handleNext = async () => {
    const success = await goToNextStep();
    if (!success) {
      // バリデーションエラーがある場合の処理は useCompanyRegistration 内で行われる
    }
  };

  // 戻るボタンの処理
  const handleBack = () => {
    goToPreviousStep();
  };

  // ステップコンポーネントのレンダリング
  const renderStepContent = () => {
    switch (currentStep) {
      case RegistrationStep.COMPANY_INFO:
        return (
          <CompanyInfoStep
            register={register}
            errors={errors}
            watchedValues={watchedValues}
            onFieldFocus={handleFieldFocus}
          />
        );
      case RegistrationStep.CONTACT_INFO:
        return (
          <ContactInfoStep
            register={register}
            errors={errors}
            watchedValues={watchedValues}
            onFieldFocus={handleFieldFocus}
          />
        );
      case RegistrationStep.CONFIRMATION:
        return (
          <ConfirmationStep
            register={register}
            errors={errors}
            watchedValues={watchedValues}
            onFieldFocus={handleFieldFocus}
            onEdit={goToStep}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card elevation={3} sx={{ maxWidth: 800, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          {/* ヘッダー */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <BusinessIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              企業アカウント登録
            </Typography>
            <Typography variant="body1" color="text.secondary">
              不動産業界の学習・採用プラットフォームへようこそ
            </Typography>
          </Box>

          {/* プログレスステップ */}
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={currentStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* エラーアラート */}
          {submitError && (
            <Alert severity="error" onClose={clearSubmitError} sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          {/* ステップコンテンツ */}
          <Box sx={{ mb: 4 }}>{renderStepContent()}</Box>

          {/* ナビゲーションボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            {/* 戻るボタン */}
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={
                currentStep === RegistrationStep.COMPANY_INFO || isSubmitting
              }
              startIcon={<ArrowBackIcon />}
              sx={{ minWidth: 120 }}
              data-testid={`back-step-${currentStep}`}
            >
              戻る
            </Button>

            {/* 次へ・送信ボタン */}
            {currentStep < RegistrationStep.CONFIRMATION ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isSubmitting}
                endIcon={<ArrowForwardIcon />}
                sx={{ minWidth: 120 }}
                data-testid={`next-step-${currentStep}`}
              >
                次へ
              </Button>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                noValidate
                sx={{ display: 'inline' }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={isSubmitting || !isValid}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )
                  }
                  sx={{
                    minWidth: 200,
                    py: 1.5,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                  data-testid="submit-registration"
                >
                  {isSubmitting ? '申請中...' : '企業登録を申請する'}
                </Button>
              </Box>
            )}
          </Box>

          {/* 注意事項（確認ステップのみ） */}
          {currentStep === RegistrationStep.CONFIRMATION && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                ※ 申請後、管理者による審査が行われます（通常3営業日）
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 登録完了ダイアログ */}
      <RegistrationSuccessDialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        companyId={registeredCompanyId}
      />
    </>
  );
};
